
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Pill, 
  Calendar, 
  BookOpen, 
  Heart, 
  Baby, 
  Bot, 
  ShoppingCart,
  X 
} from "lucide-react";

interface ToolkitModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ToolkitModal = ({ isOpen, onClose }: ToolkitModalProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [animatedCards, setAnimatedCards] = useState<boolean[]>([]);

  const tools = [
    {
      id: "medication",
      icon: Pill,
      title: "Ajouter un Médicament",
      description: "Gérez vos médicaments, dosages et rappels de prise",
      path: isMobile ? "/medicaments-mobile" : "/medicaments",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      id: "appointment",
      icon: Calendar,
      title: "Ajouter un Rendez-vous",
      description: "Planifiez et suivez vos consultations médicales",
      path: isMobile ? "/rendez-vous-mobile" : "/rendez-vous",
      gradient: "from-green-500 to-green-600"
    },
    {
      id: "journal",
      icon: BookOpen,
      title: "Accéder à votre Journal",
      description: "Tenez un journal de votre état de santé quotidien",
      path: isMobile ? "/journal-mobile" : "/journal",
      gradient: "from-purple-500 to-purple-600"
    },
    {
      id: "menstrual",
      icon: Heart,
      title: "Suivi de Cycle Menstruel",
      description: "Suivez votre cycle et vos symptômes",
      path: isMobile ? "/period-tracking-mobile" : "/period-tracking",
      gradient: "from-pink-500 to-pink-600"
    },
    {
      id: "pregnancy",
      icon: Baby,
      title: "Suivi de Grossesse",
      description: "Accompagnement pendant votre grossesse",
      path: isMobile ? "/gestation-tracking-mobile" : "/gestation-tracking",
      gradient: "from-orange-500 to-orange-600"
    },
    {
      id: "ai-assistant",
      icon: Bot,
      title: "Assistance IA Médicale",
      description: "Obtenez des conseils santé personnalisés",
      path: isMobile ? "/ai-assistance-mobile" : "/ai-assistance",
      gradient: "from-indigo-500 to-indigo-600"
    },
    {
      id: "marketplace",
      icon: ShoppingCart,
      title: "Accéder au Marketplace",
      description: "Découvrez produits et services de santé",
      path: isMobile ? "/marketplace-mobile" : "/services",
      gradient: "from-teal-500 to-teal-600"
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setAnimatedCards(new Array(tools.length).fill(false));
      tools.forEach((_, index) => {
        setTimeout(() => {
          setAnimatedCards(prev => {
            const newState = [...prev];
            newState[index] = true;
            return newState;
          });
        }, index * 100);
      });
    }
  }, [isOpen]);

  const handleToolClick = (tool: typeof tools[0], event: React.MouseEvent) => {
    const card = event.currentTarget as HTMLElement;
    
    // Animation de clic
    card.style.transform = 'scale(0.95)';
    setTimeout(() => {
      card.style.transform = '';
    }, 150);

    // Effet de ripple
    const ripple = document.createElement('div');
    const rect = card.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    
    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(79, 172, 254, 0.6);
      transform: scale(0);
      animation: ripple 0.6s linear;
      pointer-events: none;
      width: ${size}px;
      height: ${size}px;
      left: ${event.clientX - rect.left - size / 2}px;
      top: ${event.clientY - rect.top - size / 2}px;
    `;
    
    card.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
      onClose();
      navigate(tool.path);
    }, 300);
  };

  return (
    <>
      <style>{`
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }
        
        .toolkit-card {
          position: relative;
          overflow: hidden;
        }
        
        .toolkit-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transition: left 0.5s ease;
          z-index: 1;
        }
        
        .toolkit-card:hover::before {
          left: 100%;
        }
        
        .toolkit-icon {
          transition: all 0.3s ease;
        }
        
        .toolkit-card:hover .toolkit-icon {
          transform: rotate(360deg) scale(1.1);
        }
      `}</style>
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="relative p-6">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
            
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                Boîte à Outils Médicale
              </h2>
              <p className="text-gray-600 text-lg">
                Votre assistant santé personnel
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tools.map((tool, index) => {
                const Icon = tool.icon;
                return (
                  <div
                    key={tool.id}
                    className={`toolkit-card bg-white/90 backdrop-blur-sm rounded-2xl p-6 cursor-pointer transition-all duration-500 hover:transform hover:-translate-y-2 hover:scale-105 hover:shadow-2xl border border-white/20 relative ${
                      animatedCards[index] 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                    }`}
                    style={{
                      animation: animatedCards[index] 
                        ? `slideInUp 0.6s ease forwards` 
                        : 'none',
                      animationDelay: `${index * 0.1}s`
                    }}
                    onClick={(e) => handleToolClick(tool, e)}
                  >
                    <div className={`toolkit-icon w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${tool.gradient} rounded-full flex items-center justify-center relative z-10`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center relative z-10">
                      {tool.title}
                    </h3>
                    
                    <p className="text-sm text-gray-600 text-center leading-relaxed relative z-10">
                      {tool.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
